import React, {useEffect, useMemo, useRef, useState} from 'react';
import {FlexColumnSection} from "../../styles/globalStyles";
import {Spin} from "antd";
import {useSelector, useDispatch} from "react-redux";
import useScreenSize from "../../helpers/hooks/useScreenSize";
import {DB_DATE_FORMAT, LOADING_PAGE, LOADING_PAYMENT} from "@arboxappv4/shared/src/constants/constants";
import dayjs from "dayjs";
import {updateCurrStep, updateError} from "@arboxappv4/shared/src/redux/stepperSlice";
import {t} from "i18next";
import {callSiteLog, isILPhone, updatePaymentProcessInDB} from "../../helpers/functions";
import {
    openKioskTokenVerifyModal,
    paymentEnterEventListenerLog,
    paymentErrorResponseLog, paymentGotInitMessageLog,
    paymentIframeLog,
    paymentLogGroup,
    paymentNoInitMessageLog,
    paymentStopListeningResponseLog,
    paymentSubmitLog,
    paymentSuccessResponseLog, projectTypeMainSlice, projectTypes
} from "@arboxappv4/shared/src/helpers/constants";
import {
    KIOSK_TOKEN_VERIFY_MODAL, ONE_TIME_PASSWORD_MODAL,
    openModal, updateModalRes
} from "@arboxappv4/shared/src/redux/modalManagerSlice";
import {stepOptions} from "../StepOptions";
import {handlePurchasePixel} from "../FlowOptions";
import {pixelEvents} from "../../helpers/hooks/UsePixel";

const Payment = (props) => {
    const { values, setFieldValue, handleSubmit, idPropOverride, startOverride } = props;
    const project_type = useSelector(state => state.project?.project_type)
    const box = useSelector(state => state[projectTypeMainSlice[project_type]]?.box)
    const language = useSelector(state => state[projectTypeMainSlice[project_type]]?.language)
    const location = useSelector(state => state[projectTypeMainSlice[project_type]]?.selectedLocation)
    const token = useSelector(state => state.auth?.token)
    const userBox = useSelector(state => state.user?.activeUsersBoxes ?? state.kiosk?.usersBoxes)
    const membershipUser = useSelector(state => state.kiosk?.membershipUser)
    const kioskToken = useSelector(state => state.kiosk?.token)
    const uuid = useSelector(state => state.uuid?.uuid)
    const error = useSelector(state => state.stepper.error)
    const stepperConfig = useSelector(state => state.stepper)
    const kioskTokenVerifyModalRes = useSelector(state => state.modalManager[KIOSK_TOKEN_VERIFY_MODAL].processRes)
    const [loading, setLoading] = useState(true);
    const [iframeSrc, setIframeSrc] = useState(null);
    const [submitted, setSubmitted] = useState(false);
    const [showErrorScreen, setShowErrorScreen] = useState(false);
    const [gotInitMessage, setGotInitMessage] = useState(false);
    const {isMobile} = useScreenSize()
    const dispatch = useDispatch()
    const hideBack = useMemo(() => stepperConfig.steps.length === 1, [stepperConfig.steps])
    const gotInitMessageRef = useRef(gotInitMessage);

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            //if gotInitMessage is false after 9 seconds, stop payment process completely
            if(!gotInitMessageRef.current) {
                if(stepperConfig.currStep === stepOptions.PAYMENT) { //we check this to ensure we don't display an error if the user exited this step already (mostly because of sign up steps update)
                    callSiteLog(paymentNoInitMessageLog, {}, paymentLogGroup)
                    dispatch(updateError(t('payment-communication-error')))
                }
            }
        }, 9000)
        const listenToMessages = (event) => {
            if((process.env.PAYMENTS_URL ?? '').includes(event.origin)) {
                callSiteLog(paymentEnterEventListenerLog, event.data, paymentLogGroup)
            }
            if(event.data.type === "PAYMENT_RESPONSE_SUCCESS") {
                console.log('listening.. ', event.data.data)
                setTimeout(() => {
                    callSiteLog(paymentSuccessResponseLog, event.data.data, paymentLogGroup)
                    setFieldValue('payment_info', event.data.data)
                }, 10)
            }
            if(event.data.type === "PAYMENT_RESPONSE_ERROR") {
                console.log('error!!!! ', event.data)
                callSiteLog(paymentErrorResponseLog, event.data, paymentLogGroup)
                dispatch(updateError(t('payment-generic-error')))
            }
            if(event.data.type === "PAYMENT_SITE_BACK") {
                const stepIndex = stepperConfig.steps.indexOf(stepperConfig?.currStep);
                dispatch(updateCurrStep(stepperConfig.steps[stepIndex - 1]))
            }
            if(event.data.type === "PAYMENT_LISTENER_CHECK") {
                setGotInitMessage(true)
                const uuid = event.data.data?.uuidPayment
                if(uuid) {
                    updatePaymentProcessInDB({uuid, values})
                    setFieldValue('uuidPayment', uuid)
                }
                callSiteLog(paymentGotInitMessageLog, event.data, paymentLogGroup)
            }
        }

        window.addEventListener('message', listenToMessages);
        return () => {
            callSiteLog(paymentStopListeningResponseLog, {}, paymentLogGroup)
            window.removeEventListener("message", listenToMessages);
            clearTimeout(timeoutId)
            setSubmitted(false)
        };

    }, []);

    useEffect(() => {
        gotInitMessageRef.current = gotInitMessage;
    }, [gotInitMessage]);

    useEffect(() => {
        if(values.payment_info && !submitted) {
            handlePaymentSubmit()
        }
    }, [values.payment_info, submitted]);

    useEffect(() => {
        if(box && language && values && Object.keys(values).length > 0 && !error) {
            setIframeSrc(getIframeSrc())
        }
    }, [box, language, values, userBox]);


    useEffect(() => {
        if(error) {
            callSiteLog(paymentErrorResponseLog, {error, values}, paymentLogGroup)
            setFieldValue('payment_info', null)
            setIframeSrc(null)
            setSubmitted(false)
            setShowErrorScreen(true)
            // setTimeout(() => setIframeSrc(getIframeSrc()), 10)
        }
    }, [error]);

    useEffect(() => {
        callSiteLog(paymentIframeLog, {iframeSrc, values}, paymentLogGroup)
    }, [iframeSrc]);

    useEffect(() => {
        if(kioskTokenVerifyModalRes && kioskTokenVerifyModalRes.valid) {
            dispatch(updateModalRes({modalName: KIOSK_TOKEN_VERIFY_MODAL, res: null}))
            callSiteLog(paymentSubmitLog, values, paymentLogGroup)
            handleSubmit()
            setSubmitted(true)
        }
    }, [kioskTokenVerifyModalRes]);

    useEffect(() => {
        if(values.cart?.length) {
            handlePurchasePixel(values.cart, pixelEvents.BEGIN_CHECKOUT)
        }
    }, [values.cart]);

    const handlePaymentSubmit = () => {
        if(project_type === projectTypes.kiosk && values.payment_info.user_box_token) {
            callSiteLog(openKioskTokenVerifyModal, values, paymentLogGroup)
            dispatch(openModal({modalName: KIOSK_TOKEN_VERIFY_MODAL, props: {},
                onClose: ({fromClose = false}) => {
                    if(fromClose) {
                        setIframeSrc(null)
                        setTimeout(() => setIframeSrc(getIframeSrc()), 100)
                    }
                }}))
        } else {
            setSubmitted(true)
            callSiteLog(paymentSubmitLog, values, paymentLogGroup)
            handleSubmit()
        }
    }

    // name and phone are only for meshulam
    const getUserName = () => {
        const firstName = userBox?.first_name ?? values?.first_name
        const lastName = userBox?.last_name ?? values?.last_name
        if(firstName && lastName) {
            if(firstName.length >= 2 && lastName.length >= 2) {
                return `&name=${firstName} ${lastName}`
            }
        }

        return ''
    }

    const getUserPhone = () => {
        const phone = userBox?.phone ?? values?.phone
        if(phone && isILPhone(phone) && phone.length >= 10) {
            return `&phone=${phone}`
        }
        return ''
    }

    const getQuantity = () => {
        if(values.cart && values.cart.length) {
            const quantities = values.cart.reduce((acc, item) => {
                if(item.quantity > 1) {
                    return {...acc, [item.id]: item.quantity}
                }
                return acc
            }, {})
            if(Object.keys(quantities).length) {
                return `&quantity=${JSON.stringify(quantities)}`
            }
        }
        return ''
    }

    const getStartDates = () => {
        const today = dayjs().format(DB_DATE_FORMAT)
        if(values.cart && values.cart.length) {
            const startDates = values.cart.reduce((acc, item) => {
                if(item?.start && (item.start > today)) {
                    return {...acc, [item.id]: item.start}
                }
                return acc
            }, {})
            if(Object.keys(startDates).length) {
                return `&startDates=${JSON.stringify(startDates)}`
            }
        } else if (startOverride) {
            return `&startDates=${JSON.stringify({[idPropOverride ? values[idPropOverride] : values.cart?.id]: startOverride})}`
        }
        return ''
    }

    const getCartIds = () => {
        if(idPropOverride) return [values[idPropOverride]]
        else if(project_type === projectTypes.kiosk) return []
        else {
            return values.cart?.map(item => item.id)
        }
    }

    const getKioskParams = () => {
        let params = ''
        if(project_type === projectTypes.kiosk) {
            params += `&kioskToken=${kioskToken}&visitorUuid=${uuid}`
            if(membershipUser) {
                params += `&membershipUserId=${membershipUser.id}`
            }
        }
        return params
    }

    const getIframeSrc = () => {
        console.log('payment location is ', values.locations_box_fk ?? location.id)
        return `${process.env.PAYMENTS_URL}?box_id=${box.id}&lang=${language?.code}&locations_box_id=${values.locations_box_fk ?? location.id}&cart=${JSON.stringify(getCartIds())}&token=${token}&hideBack=${hideBack}${getUserName()}${getUserPhone()}${getQuantity()}${getStartDates()}${getKioskParams()}`
    }

    return (
       <FlexColumnSection padding={isMobile ? '0 16px' : 0} align={'start'}>
           <Spin spinning={loading} style={{alignSelf: 'center', justifyContent:'center', flex: 1}}/>
           {iframeSrc &&
           <iframe frameBorder={0}
                   style={{width: '100%', flex: 1}}
                   onLoad={() => setLoading(false)}
                   src={iframeSrc}/>}
           {showErrorScreen && <div>{t('payment-error-screen-message')}</div>}
       </FlexColumnSection>
    );
};

export default Payment;